import {AfterViewInit, Component, ElementRef, Inject, Injector, OnInit, Type, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import { NgComponentOutlet, CommonModule } from '@angular/common';
import {COMMON_FORM_IMPORTS} from "../../shared/import-groups/Common";
import {CdkDrag} from "@angular/cdk/drag-drop";

export interface CustomDialogData {
    form_result: string;
    config: CUSTOM_DIALOG_CONFIG;
    positionRelativeToElement: HTMLElement | null;

    setStyles(event: any): any;

    selectionChanged(event: any): any;
}

interface IPositionType {
    left: number;
    top: number;
    adjust_left: number;
}

type OptionalPositionType = Partial<IPositionType>;

export interface ICUSTOM_DIALOG_CONFIG {
    component: string;
    parameters: any;
    position: OptionalPositionType;
    show_close: boolean;
}

export type CUSTOM_DIALOG_CONFIG = Partial<ICUSTOM_DIALOG_CONFIG>;

@Component({
    selector: 'custom-dialog',
    templateUrl: './custom-dialog.component.html',
    imports: [CommonModule, NgComponentOutlet, COMMON_FORM_IMPORTS, CdkDrag],
    styleUrls: ['./custom-dialog.component.less']
})
export class CustomDialogComponent implements OnInit, AfterViewInit {
    config: CUSTOM_DIALOG_CONFIG;
    componentType: Type<unknown> | null = null;
    componentInputs: Record<string, any> = {};
    private positionRelativeToElement: HTMLElement;
    @ViewChild('custom_dialog_container') container: ElementRef;

    constructor(
        public dialogRef: MatDialogRef<any>,
        @Inject(MAT_DIALOG_DATA) private dialogData: CustomDialogData,
        private injector: Injector
    ) {
    }

    async ngOnInit() {
        this.config = this.dialogData.config;

        const name = this.config.component;
        switch (name) {
            case 'SeriesAuditHistoryComponent': {
                const mod = await import('../../components/series-audit-history/series-audit-history.component');
                this.componentType = mod.SeriesAuditHistoryComponent;
                this.componentInputs = {series: this.config.parameters.series};
                break;
            }
            case 'CommentComponent': {
                this.componentType = null; // handled manually in template
                break;
            }
            case 'LimitsPrompt': {
                const mod = await import('../../components/snackbar/constant-limits-prompt/constant-limits-prompt.component');
                this.componentType = mod.ConstantLimitsPromptComponent;
                this.componentInputs = {
                    limits: this.config.parameters.limits,
                    value: this.config.parameters.value,
                    dialogRef: this.dialogRef,
                    cpColumn: this.config.parameters.cp_column,
                    component: this.config.parameters.component
                };
                break;
            }
            case 'AuditMixinComponent': {
                const mod = await import('../../components/audit-mixin/audit-mixin.component');
                this.componentType = mod.AuditMixinComponent;
                this.componentInputs = {
                    model: this.config.parameters.model,
                    showTitle: this.config.parameters.showTitle
                };
                break;
            }
            case 'ColumnWidth': {
                this.componentType = null; // handled manually in template
                break;
            }
            default:
                console.warn(`CustomDialogComponent: unhandled component type '${name}'. If this was recently migrated to PopupService, check calling code.`);
                this.componentType = null;
        }
    }

    ngAfterViewInit() {
        this.positionRelativeToElement = this.dialogData.positionRelativeToElement;
        if (this.positionRelativeToElement) {
            this.updateDialogPosition();
        }
    }

    updateDialogPosition() {
        const matDialogConfig = new MatDialogConfig();
        const rect: DOMRect = this.positionRelativeToElement.getBoundingClientRect();
        let left = 2;
        let top = 2;
        let adjust_left = 0;
        let screenWidth = window.innerWidth;
        let screenHeight = window.innerHeight;

        if (this.config.position) {
            left = this.config.position.left || 0;
            top = this.config.position.top || 0;
            adjust_left = this.config.position.adjust_left || 0;
        }
        if ((left + this.container.nativeElement.clientWidth + rect.left) >= screenWidth) {
            left = screenWidth - this.container.nativeElement.clientWidth - rect.left;
        } else if (left < 0) {
            left = 10;
        }
        if ((top + this.container.nativeElement.clientHeight + rect.top) >= screenHeight) {
            top = screenHeight - this.container.nativeElement.clientHeight - rect.top;
        } else if (top < 0) {
            top = 10;
        }

        matDialogConfig.position = {left: `${rect.left + left + adjust_left}px`, top: `${rect.top + top}px`};
        this.dialogRef.updatePosition(matDialogConfig.position);
    }

    isDraggable(component: string | undefined): boolean {
        return ['AuditMixinComponent'].includes(component ?? '');
    }

    updateSeriesSummary(event: any) {
        this.dialogData.setStyles(event);
    }

    updateSelectedValue(event: any, close = true) {
        this.dialogData.selectionChanged(event);
        if (!close) return;
        this.onCloseClick();
    }

    updateText(event: any) {
        this.dialogData.setStyles(event.target.value);
    }

    onCloseClick(): void {
        this.dialogRef.close();
    }
}
